/**
 * Shared chat types used by Ask Argus components.
 *
 * These mirror the legacy `Mock*` shapes that the UI components were
 * historically built against, but they are now defined here (no mock
 * data dependency) so the components can stay decoupled from
 * `mock-chat-data.ts` while we wire them to real APIs.
 */

export interface Citation {
  docName: string;
  page: number | null;
  section: string | null;
  text: string;
  /**
   * Retrieval / re-ranker score for this chunk. Optional because the
   * server-side `citation` SSE event does not always include a score
   * (live-retrieved chunks may carry only docName/page/section/text).
   * Renderers MUST guard with `Number.isFinite(score)` before using it
   * in arithmetic — see context-panel.tsx + #1086.
   */
  score?: number;
  classification?: string;
  documentId?: string;
  /**
   * Source-attribution tier (source-attribution feature). 'library' (blue,
   * verified customer knowledge) | 'ai' (amber, unverified model knowledge) |
   * 'web' (teal, unverified web result). Defaults to 'library' for legacy
   * citations (set in extractCitations). Drives the tier colour + legend.
   */
  sourceType?: 'library' | 'ai' | 'web';
  /** Sub-source label within a tier — connector / MCP-server / web engine. */
  subSource?: string | null;
  /** Outbound link for web citations. */
  url?: string | null;
  /**
   * 1-based position this source had in the list the MODEL was shown — the N
   * an answer's "[Source N]" marker refers to. Stamped server-side BEFORE
   * uncited sources are dropped (query-pipeline.service.ts), so a marker still
   * resolves to the right document after filtering. MarkdownRenderer resolves
   * markers by this field first and by array position only for legacy
   * messages that carry no stamp (review BF-01).
   */
  sourceIndex?: number;
}

export interface ToolCall {
  toolName: string;
  args: Record<string, unknown>;
  result: unknown;
  durationMs: number;
  status: 'success' | 'failed' | 'pending' | 'approval_required';
  policyAction: 'auto_approve' | 'require_approval' | 'forbidden';
  /**
   * Wave 2D — UUID of the agent_tool_approvals row. Present only when
   * policyAction is 'require_approval' and the backend has created the
   * pending approval record. Passed to ApprovalCard to wire Approve/Deny.
   */
  approvalId?: string;
  /** Human-readable summary of what the approval is for (shown in ApprovalCard). */
  approvalSummary?: string;
}

export interface PipelineStage {
  name: string;
  durationMs: number;
  status: 'success' | 'failed' | 'skipped' | 'pending';
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /**
   * feat/multi-agent-in-chat — slug of the agent that authored this
   * assistant message. When undefined, the UI treats the message as
   * Ask Argus (the default bubble bucket). Used by `chat-messages.tsx`
   * to group sequential bubbles per agent when multiple specialists
   * respond in one turn.
   */
  agentId?: string;
  /**
   * feat/multi-agent-in-chat — pre-populated display metadata from
   * the dispatcher's `metadata` event (display name + kind). Lets the
   * UI render the right avatar + label even before tokens arrive.
   * Populated on assistant messages only.
   */
  agentDisplayName?: string;
  agentKind?: string;
  citations?: Citation[];
  toolCalls?: ToolCall[];
  confidenceScore?: number;
  /**
   * Optional rationale text shown in the confidence chip tooltip
   * (PR-AAU-A SSE `confidence` event). When `'placeholder'`, the UI
   * renders the "No data" tier (paused) instead of a real score.
   */
  confidenceRationale?: string;
  thinkingContent?: string;
  /**
   * Pipeline trace for the response (only populated when the backend
   * emits `pipeline` SSE events). When `undefined` the trace drawer
   * is hidden.
   */
  pipeline?: PipelineStage[];
  metadata?: {
    model?: string;
    totalTokens?: number;
    latencyMs?: number;
    intent?: string;
    ragSourceCount?: number;
    /**
     * Row id of the persisted assistant message, reported on `done`. The
     * streamed bubble keeps its synthetic local id as the React key (follow-ups
     * arrive after `done` and still address it), so the real id rides here and
     * is what feedback is submitted against.
     */
    serverMessageId?: string;
  };
  flags?: string[];
  /**
   * bug #13 — answer-quality banner from the backend gate's `banner` SSE
   * event. Rendered as an "ungrounded / not independently verified" notice
   * above the message body when present.
   */
  qualityBanner?: { level: string; kind: string; message: string };
  /**
   * bug #23 — proactive follow-up suggestions from the `follow_up` SSE event,
   * rendered as clickable chips under the assistant message.
   */
  followUps?: string[];
  feedback?: { rating: 'positive' | 'negative'; reason?: string };
  /**
   * Terminal-error state for this turn (PR-AAU-C). When set, the
   * message bubble renders an error card with Retry + Copy actions
   * instead of normal content / loading.
   */
  error?: {
    code?: string;
    message: string;
    raw?: unknown;
  };
  /**
   * The original user prompt that produced this assistant message —
   * captured at dispatch time so the error card's "Retry" button can
   * re-send it. Only populated on assistant messages.
   */
  retryPrompt?: string;
  timestamp: Date;
}

export interface Conversation {
  id: string;
  title: string;
  pinned: boolean;
  updatedAt: Date;
  messageCount: number;
  projectId?: string;
  agentId?: string;
}

/**
 * Group conversations into time buckets for the sidebar.
 * Pinned conversations are excluded (the sidebar renders them in a
 * separate "Pinned" section).
 */
export function groupConversationsByDate(
  convs: Conversation[],
): Record<string, Conversation[]> {
  const groups: Record<string, Conversation[]> = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(today);
  monthAgo.setDate(monthAgo.getDate() - 30);

  for (const conv of convs) {
    if (conv.pinned) continue;
    const t = conv.updatedAt.getTime();
    let group: string;
    if (t >= today.getTime()) group = 'Today';
    else if (t >= yesterday.getTime()) group = 'Yesterday';
    else if (t >= weekAgo.getTime()) group = 'Previous 7 Days';
    else if (t >= monthAgo.getTime()) group = 'Previous 30 Days';
    else group = 'Older';

    if (!groups[group]) groups[group] = [];
    groups[group].push(conv);
  }
  return groups;
}
